// @flow
import BeEndpoints from '../../services/end-points/be-endpoints';
import type { Request, ResponseState } from '../services/useRequest';

const SalesApiErrorCodes = {
  SESSION_EXPIRED: 1402,
  AGENT_BLOCKED: 4070,
};

Object.freeze(SalesApiErrorCodes);

export { SalesApiErrorCodes };

export type ErrorResponseType = {
  errorCode: number,
  title: string,
  message: string,
};

export type EMIInfoType = {
  emiPlanId: string,
  tenure: number,
  interestRate: number,
  currency: 'INR',
  emi: number,
  upfrontDiscountedAmount: number,
  totalOfferDiscountAmount: number,
  bankInterestRate: number,
  totalInterestPayableToBank: number,
  totalAmountPayableToBank: number,
  bankFee: number,
  planOfferType: string,
  purchaseAmount: number,
};

export type LenderDetailsType = {
  lenderId: number,
  name: string,
  logo: string,
  logoIcon: string,
  category: string,
  maxNtbLimit?: number,
  maxTenure?: number,
  minLoanAmount?: number,
  maxLoanAmount?: number,
  availableOffers?: string[],
  emiPlans?: EMIInfoType,
  minInterestRate: number,
};

export type CartInfoType = {
  sku: string,
  EAN: string,
  quantity: number,
  unitPrice: number,
  itemUrl: string,
  category: [],
};

export type CheckEligibilityRequestType = {
  mobile: string,
  amount?: string,
  cartInfo?: CartInfoType,
};

export type CheckEligibilityResponseType = {
  isEligible: boolean,
  isNtbAllowed: boolean,
  isPreApproved: boolean,
  emiLenders?: LenderDetailsType[],
  ntbLenders?: LenderDetailsType[],
};

export type PreCheckEligibilityRequestType = {
  mobile: string,
  amount?: string,
  cartInfo?: CartInfoType,
};

export type PreCheckEligibilityResponseType = {
  isPreApproved: boolean,
  isNtbAllowed: boolean,
  isNtbApplicationInProgress: boolean,
};

export type GetEmiDetailsRequestType = {
  amount: number,
  cartInfo?: CartInfoType,
};

export type SendNtbApplicationRequestType = {
  mobile: string,
  lenderId: number,
  email?: string,
  userName: string,
};

export type SendNtbApplicationResponseType = {
  ntbLink: string,
  whatsAppLink: string,
};

export type GetEmiDetailsResponseType = {
  emiLenders: LenderDetailsType[],
};

export type InitResponseType = {
  merchantName: string,
  merchantLogo: string,
  merchantTheme: {
    backgroundColor: string,
    fontColor: string,
    primaryCTAColor: string,
    secondaryCTAColor: string,
  },
};

const salesCheckEligibility = (
  makeRequest: (
    requestConfig: Request<CheckEligibilityRequestType>,
  ) => Promise<ResponseState<CheckEligibilityResponseType>>,
  checkEligibilityRequest: CheckEligibilityRequestType,
) => {
  const request = {
    path: `${BeEndpoints.SALES_CHECK_ELIGIBILITY}`,
    method: 'POST',
    data: checkEligibilityRequest,
  };
  return makeRequest(request);
};

const salesPreCheckEligibility = (
  makeRequest: (
    requestConfig: Request<PreCheckEligibilityRequestType>,
  ) => Promise<ResponseState<PreCheckEligibilityResponseType>>,
  preCheckEligibilityRequest: PreCheckEligibilityResponseType,
) => {
  const request = {
    path: `${BeEndpoints.SALES_PRE_CHECK_ELIGIBILITY}`,
    method: 'POST',
    data: preCheckEligibilityRequest,
  };
  return makeRequest(request);
};

const salesGetEmiDetails = (
  makeRequest: (
    requestConfig: Request<GetEmiDetailsRequestType>,
  ) => Promise<ResponseState<GetEmiDetailsResponseType>>,
  getEmiDetailsRequest: GetEmiDetailsRequestType,
) => {
  const request = {
    path: `${BeEndpoints.SALES_GET_EMI_DETAILS}`,
    method: 'POST',
    data: getEmiDetailsRequest,
  };
  return makeRequest(request);
};

const sendOtpAPI = (
  makeRequest: (
    requestConfig: Request<{ mobile: string }>,
  ) => Promise<ResponseState<>>,
  mobile: string,
) => {
  const request = {
    path: `${BeEndpoints.SALES_SEND_SHARE_CODE}`,
    method: 'POST',
    data: {
      mobile,
    },
  };
  return makeRequest(request);
};

const verifyOtpAPI = (
  makeRequest: (
    requestConfig: Request<{ mobile: string, otp: string }>,
  ) => Promise<ResponseState<>>,
  mobile: string,
  otp: string,
) => {
  const request = {
    path: `${BeEndpoints.SALES_VERIFY_SHARE_CODE}`,
    method: 'POST',
    data: {
      mobile,
      otp,
    },
  };
  return makeRequest(request);
};

const sendNtbApplicationLink = (
  makeRequest: (
    requestConfig: Request<SendNtbApplicationRequestType>,
  ) => Promise<ResponseState<SendNtbApplicationResponseType>>,
  sendNtbApplicationRequest: SendNtbApplicationRequestType,
) => {
  const request = {
    path: `${BeEndpoints.SALES_SEND_NTB_SMS}`,
    method: 'POST',
    data: sendNtbApplicationRequest,
  };
  return makeRequest(request);
};

const fetchSalesAppConfig = (
  makeRequest: (
    requestConfig: Request,
  ) => Promise<ResponseState<InitResponseType>>,
  headers,
) => {
  const request = {
    path: `${BeEndpoints.SALES_APP_INIT}`,
    method: 'GET',
    headers: headers,
  };
  return makeRequest(request);
};

export {
  salesCheckEligibility,
  salesPreCheckEligibility,
  salesGetEmiDetails,
  sendOtpAPI,
  verifyOtpAPI,
  sendNtbApplicationLink,
  fetchSalesAppConfig,
};



// WEBPACK FOOTER //
// ./api/controllers/salesController.js